import React, { useEffect, useRef, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import {
  Button,
  Divider,
  Paper,
  Typography,
  Grid,
  LinearProgress,
  Select,
  MenuItem,
  FormControl,
  Box,
  IconButton,
  Popover,
  CircularProgress,
  Alert,
} from "@mui/material";
import AccessControl from "../../components/AccessControl";
import {
  getAllDealStages,
  getDeals,
  getCurrencies,
  getDealPipelines,
  setSelectedPipe,
  getAPipeline,
  getStagerulesBased,
  getDealTemplates,
  getStageTransfer,
  RESET_DEAL_TEMPLATES,
  updateADeal,
} from "../../redux/modules/deals";
import { getAllAccountAndSetFirst } from "../../redux/modules/leads";
import DealItem from "./components/DealItem";
import CreateDeal from "./components/CreateDeal";
import { Helmet } from "react-helmet";
import EmailIcon from "@mui/icons-material/Email";
import OverrideRulesModal from "./components/OverrideRulesModal";
import { formatTime } from "../../utils/timeConverter";
import AccessTimeIcon from "@mui/icons-material/AccessTime";
import SettingsBackupRestoreIcon from "@mui/icons-material/SettingsBackupRestore";
import SettingsIcon from "@mui/icons-material/Settings";
import MoveDownIcon from "@mui/icons-material/MoveDown";
import CloseIcon from "@mui/icons-material/Close";
import { getBTemplates } from "../../redux/modules/email";
import { DragDropContext, Droppable, Draggable } from "react-beautiful-dnd";
import { setAlert } from "../../redux/modules/alert";
import BadgeIcon from "@mui/icons-material/Badge";
function Deals() {
  const scrollContainer = useRef();
  const initialLoad = useRef(true);
  const dispatch = useDispatch();
  const dealStages = useSelector((state) => state.deals.dealStages);
  const selectedPipe = useSelector((state) => state.deals.selectedPipe);
  const deals = useSelector((state) => state.deals.deals);
  const accounts = useSelector((state) => state.leads.accounts);
  const pipelines = useSelector((state) => state.deals.pipelines);
  const selectedPipeStages = useSelector(
    (state) => state.deals.selectedPipeStages
  );
  const currencies = useSelector((state) => state.deals.currencies);
  const [loading, setLoading] = useState(false);
  const [openModal, setOpenModal] = useState(false);
  const [anchorEl, setAnchorEl] = useState(null);
  const [selectedStage, setSelectedStage] = useState(null);
  const [stageRulesData, setStageRulesData] = useState([]);
  const [ruleDataLoading, setRuleDataLoading] = useState(false);
  const [renderDeal, setRenderDeal] = useState(0);
  const [localDeals, setLocalDeals] = useState([]);

  useEffect(() => {
    let initialPromises = [];
    setLoading(true);
    dispatch(setSelectedPipe("all"));

    initialPromises.push(dispatch(getAllDealStages()));
    initialPromises.push(dispatch(getDeals()));
    if (!pipelines.length) {
      initialPromises.push(dispatch(getDealPipelines()));
    }

    if (!accounts.length) {
      initialPromises.push(dispatch(getAllAccountAndSetFirst()));
    }

    if (!currencies.length) {
      initialPromises.push(dispatch(getCurrencies()));
    }

    Promise.all(initialPromises).then(() => {
      setLoading(false);
    });
  }, []);

  useEffect(() => {
    if (!initialLoad.current) {
      setLoading(true);
      dispatch(getDeals(selectedPipe))
        .then(() => {
          setLoading(false);
        })
        .catch(() => {
          setLoading(false);
        });
    }

    initialLoad.current = false;
  }, [selectedPipe, renderDeal]);

  useEffect(() => {
    setLocalDeals(deals);
  }, [deals]);

  const onPipeChange = (e) => {
    dispatch(setSelectedPipe(e.target.value || "all"));

    if (e.target.value) {
      dispatch(getAPipeline(e.target.value));
    }
  };

  const onWheel = (e) => {
    // scrollContainer.current.scrollLeft += e.deltaY;
  };

  const availableStages =
    selectedPipe != "all" ? selectedPipeStages : dealStages;

  const handleClick = (event, stage) => {
    setAnchorEl(event.currentTarget);
    setSelectedStage(stage);
    setRuleDataLoading(true);
    let loadPromises = [];
    loadPromises.push(
      dispatch(getStagerulesBased(stage?.stage_id, selectedPipe))
    );
    Promise.all(loadPromises)
      .then((response) => {
        const apiData = response?.[0]?.rules || [];
        setStageRulesData(apiData);
        dispatch(getBTemplates());
        setRuleDataLoading(false);
      })
      .catch((error) => {
        setStageRulesData([]);
        setRuleDataLoading(false);
      });
  };

  const handleClose = () => {
    setAnchorEl(null);
    setSelectedStage(null);
  };

  const open = Boolean(anchorEl);

  const handleOverRideModal = () => {
    setOpenModal(true);
    // let loadPromises = [];
    // loadPromises.push(dispatch(getBTemplates()));
    // Promise.all(loadPromises)
    //   .then(() => {
    //     setLoading(false);
    //     setOpenModal(true);
    //   })
    //   .catch(() => {
    //     // setLoading(false);
    //   });
  };

  const onDragEnd = async (result) => {
    const { destination, source, draggableId } = result;

    if (!destination) return; // dropped outside
    if (
      destination.droppableId === source.droppableId &&
      destination.index === source.index
    )
      return; // dropped in same position

    const draggedDealId = draggableId;
    const newStageId = destination.droppableId;
    const prevStageId = source.droppableId;

    // Only update API if stage changed
    const stageChanged = newStageId !== prevStageId;

    //  Optimistic update (always update UI)
    setLocalDeals((prev) =>
      prev.map((deal) =>
        deal.deal_id.toString() === draggedDealId
          ? { ...deal, deal_stage: newStageId }
          : deal
      )
    );

    if (!stageChanged) return; // skip API if stage didn't change

    try {
      //  Call API only when stage changed
      await dispatch(
        updateADeal({
          deal_id: draggedDealId,
          deal_stage: newStageId,
        })
      );
      setRenderDeal((prev) => prev + 1);
    } catch (error) {
      console.error("Failed to update stage", error);
      //  Revert if API fails
      setLocalDeals((prev) =>
        prev.map((deal) =>
          deal.deal_id.toString() === draggedDealId
            ? { ...deal, deal_stage: prevStageId }
            : deal
        )
      );
      console.error("Failed to update stage. Please try again.");
    }
  };

  return (
    <>
      <Helmet>
        <title>Deals</title>
        <meta name="description" content="Deals page" />
      </Helmet>
      <AccessControl name="view_deals" message>
        <div className="deals_container">
          <div className="page_t_head">
            <div className="page_d_wrapper">
              <Typography className="page_title">Deals</Typography>
              <AccessControl name="create_deals">
                <CreateDeal
                  renderDeal={renderDeal}
                  setRenderDeal={setRenderDeal}
                />
              </AccessControl>
            </div>
            <div className="lp-deals">
              {loading ? <LinearProgress /> : null}
            </div>
          </div>
          <div className="deal-toolbar">
            <FormControl variant="outlined" color="primary">
              <Select
                style={{ maxHeight: 40 }}
                margin="dense"
                defaultValue="all"
                value={selectedPipe || "all"}
                onChange={onPipeChange}
              >
                <MenuItem value="all">All Pipelines</MenuItem>
                {pipelines?.map((pl, l) => {
                  return (
                    <MenuItem key={l} value={pl.pipeline_id}>
                      {pl.pipeline_name}
                    </MenuItem>
                  );
                })}
              </Select>
            </FormControl>
          </div>
          <div className="deals-view-container">
            <DragDropContext onDragEnd={onDragEnd}>
              <div className="deals-view-funnel">
                {availableStages.length ? (
                  availableStages.map((stage, i) => {
                    // const filteredDeals = deals.filter(
                    //   (d) => d.deal_stage === stage.stage_id
                    // );
                    const filteredDeals = localDeals?.filter(
                      (d) => d.deal_stage === stage.stage_id
                    );

                    return (
                      <>
                        <Paper className="deals-view-funnel-item">
                          <Box
                            key={i}
                            sx={{
                              display: "flex",
                              justifyContent: "space-between",
                            }}
                          >
                            <Typography
                              className="stage-name-head"
                              variant="h6"
                            >
                              {stage.stage_name} ({filteredDeals.length})
                            </Typography>
                            {selectedPipe !== "all" && (
                              <IconButton
                                onClick={(e) => handleClick(e, stage)}
                                size="small"
                                color="primary"
                                disabled={i === availableStages?.length - 1}
                              >
                                <EmailIcon />
                              </IconButton>
                            )}
                          </Box>

                          <Divider style={{ marginBottom: 15 }} />

                          <Droppable
                            droppableId={stage.stage_id.toString()}
                            key={stage?.stage_id}
                          >
                            {(provided, snapshot) => (
                              <div
                                className="deals-list"
                                ref={provided.innerRef}
                                {...provided.droppableProps}
                                style={{
                                  display: "flex",
                                  flexDirection: "column",
                                  minHeight: 60, // allows dropping into empty list
                                  gap: 8,
                                  background: snapshot.isDraggingOver
                                    ? "#f0f8ff"
                                    : "white",
                                }}
                              >
                                {filteredDeals?.length ? (
                                  <div>
                                    {filteredDeals.map((ds, j) => {
                                      const taggedAccount = accounts?.find(
                                        (acc) =>
                                          acc.account_id === ds.contact_account
                                      );

                                      return (
                                        <Draggable
                                          key={ds.deal_id.toString()}
                                          draggableId={ds.deal_id.toString()}
                                          index={j}
                                        >
                                          {(provided, snapshot) => (
                                            <div
                                              ref={provided.innerRef}
                                              {...provided.draggableProps}
                                              {...provided.dragHandleProps}
                                              style={{
                                                ...provided.draggableProps
                                                  .style,
                                                opacity: snapshot.isDragging
                                                  ? 0.8
                                                  : 1,
                                              }}
                                            >
                                              <DealItem
                                                deal={ds}
                                                stage={stage.stage_name}
                                                account={taggedAccount}
                                                currencies={currencies}
                                                isOverride={ds.override === "1"}
                                                selectedPipe={selectedPipe}
                                                renderDeal={renderDeal}
                                                setRenderDeal={setRenderDeal}
                                                disabled={
                                                  i ===
                                                  availableStages?.length - 1
                                                }
                                              />
                                            </div>
                                          )}
                                        </Draggable>
                                      );
                                    })}
                                  </div>
                                ) : (
                                  <Typography className="no-deals-message">
                                    No deals in this stage
                                  </Typography>
                                )}
                                {provided.placeholder}

                                {filteredDeals.length === 0 && (
                                  <Typography
                                    style={{
                                      textAlign: "center",
                                      color: "#aaa",
                                    }}
                                    variant="caption"
                                  >
                                    Drop deals here
                                  </Typography>
                                )}
                              </div>
                            )}
                          </Droppable>
                        </Paper>
                      </>
                    );
                  })
                ) : loading ? (
                  ""
                ) : (
                  <Typography
                    className="empty-stages-message"
                    component="div"
                    variant="caption"
                  >
                    No deals and deal stages available to show. Add new deal
                    stages and pipelines from settings.
                  </Typography>
                )}
              </div>
            </DragDropContext>
          </div>
        </div>
      </AccessControl>

      <Popover
        open={open}
        anchorEl={anchorEl}
        onClose={handleClose}
        anchorOrigin={{
          vertical: "bottom",
          horizontal: "left",
        }}
      >
        <Box p={2} maxWidth={300} position="relative">
          {/* Close Button */}
          <IconButton
            size="small"
            onClick={handleClose}
            sx={{
              position: "absolute",
              right: 4,
              top: 4,
            }}
          >
            <CloseIcon fontSize="small" />
          </IconButton>

          {ruleDataLoading ? (
            <Box
              display="flex"
              justifyContent="center"
              alignItems="center"
              minHeight={150}
              maxWidth={300}
              width={200}
            >
              <CircularProgress size={24} />
            </Box>
          ) : stageRulesData?.length > 0 ? (
            <>
              <Typography variant="subtitle1" fontWeight="bold">
                📧 Pipeline Follow-Up Rules
              </Typography>
              <Divider sx={{ my: 1 }} />

              <Box display="flex" alignItems="center" mb={1}>
                <EmailIcon sx={{ mr: 1 }} />
                <Typography variant="body2">
                  <strong>Email Template:</strong>{" "}
                  {stageRulesData?.[0]?.deal_template_name || "—"}
                </Typography>
              </Box>

              <Box display="flex" alignItems="center" mb={1}>
                <AccessTimeIcon sx={{ mr: 1 }} />
                <Typography variant="body2">
                  <strong>Send After:</strong>{" "}
                  {formatTime(stageRulesData?.[0]?.follow_up_time)}
                </Typography>
              </Box>
              <Box display="flex" alignItems="center" mb={1}>
                <MoveDownIcon sx={{ mr: 1 }} />
                <Typography variant="body2">
                  <strong>Auto-Move:</strong> After{" "}
                  {formatTime(stageRulesData?.[0]?.stage_transition_time)} to{" "}
                  <em>{stageRulesData?.[0]?.deal_stage_to_name}</em>
                </Typography>
              </Box>
              {stageRulesData?.[0]?.sender_name && (
                <Box display="flex" alignItems="center" mb={2}>
                  <BadgeIcon sx={{ mr: 1 }} />
                  <Typography variant="body2">
                    <strong>From name:</strong>&nbsp;
                    {stageRulesData?.[0]?.sender_name}
                  </Typography>
                </Box>
              )}
              <Button
                fullWidth
                variant="outlined"
                size="small"
                onClick={handleOverRideModal}
                endIcon={<SettingsBackupRestoreIcon />}
              >
                Override Rules
              </Button>
              <Alert severity="info" sx={{ mt: 2 }}>
                The pipeline rules are applicable to newly created deals.
              </Alert>
            </>
          ) : (
            <Typography width={170}>
              <Button
                sx={{ width: 150 }}
                fullWidth
                variant="outlined"
                size="small"
                onClick={handleOverRideModal}
                endIcon={<SettingsIcon />}
              >
                Configure rule
              </Button>
            </Typography>
          )}
        </Box>
      </Popover>
      <OverrideRulesModal
        open={openModal}
        handleClose={() => setOpenModal(false)}
        availableStages={availableStages}
        selectedStage={{
          ...selectedStage,
          rule_id: stageRulesData?.[0]?.id,
          pipeline_id: selectedPipe,
        }}
        selectedPipeStages={selectedPipeStages}
        handleCloseOverRide={handleClose}
        stageRulesData={stageRulesData}
      />
    </>
  );
}

export default Deals;
